import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { documentsApi, projectsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  Upload,
  FileText,
  Image,
  File,
  Trash2,
  Download,
  FolderKanban,
  ChevronRight,
  User,
  Eye,
  X,
  Camera,
  FolderOpen,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";

const FILE_ICONS = {
  image: Image,
  pdf: FileText,
  default: File,
};

const getFileType = (filename) => {
  const ext = (filename || "").split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "bmp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "default";
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Documents = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const [activeTab, setActiveTab] = useState("images");
  const [imageSearch, setImageSearch] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) loadDocuments();
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const res = await projectsApi.getAll();
      const active = res.data.filter(p => p.is_active);
      setProjects(active);
      // Restore last project from localStorage
      const saved = localStorage.getItem("malair_docs_project");
      if (saved && active.find(p => p.id === saved)) {
        setSelectedProject(saved);
      } else if (active.length > 0) {
        setSelectedProject(active[0].id);
      }
    } catch (error) {
      toast.error("Kunde inte ladda projekt");
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const res = await documentsApi.getByProject(selectedProject);
      setDocuments(res.data);
    } catch (error) {
      toast.error("Kunde inte ladda dokument");
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!selectedProject) {
      toast.error("Välj ett projekt först");
      return;
    }

    setUploading(true);
    try {
      let count = 0;
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`${file.name} är för stor (max 20MB)`);
          continue;
        }
        await documentsApi.upload(selectedProject, file, description);
        count++;
      }
      if (count > 0) {
        toast.success(`${count} ${count === 1 ? "fil" : "filer"} uppladdad${count > 1 ? "e" : ""}`);
        setDescription("");
        loadDocuments();
      }
    } catch (error) {
      toast.error("Kunde inte ladda upp");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (docId) => {
    try {
      await documentsApi.delete(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success("Borttagen");
    } catch (error) {
      toast.error("Kunde inte ta bort");
    }
  };

  const handlePreview = (doc) => {
    const token = localStorage.getItem("token");
    const url = `${documentsApi.download(doc.id)}?token=${token}`;
    setPreviewError(false);
    setPreviewUrl(url);
    setPreviewName(doc.filename);
  };

  const handleDownload = (doc) => {
    const token = localStorage.getItem("token");
    const url = `${documentsApi.download(doc.id)}?token=${token}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.filename;
    a.click();
  };

  const currentProject = projects.find(p => p.id === selectedProject);
  const images = documents.filter(d => getFileType(d.filename) === "image");
  const files = documents.filter(d => getFileType(d.filename) !== "image");

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6dbf4a]"></div></div>;
  }

  return (
    <div className="space-y-5 slide-up" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-[#6dbf4a]" />
            <h1 className="text-2xl font-bold text-white tracking-tight">Dokument</h1>
          </div>
          <p className="text-white/25 text-sm">Ritningar, foton och filer per projekt</p>
        </div>
      </div>

      {/* Project selector + Upload */}
      <div className="flex gap-3 items-center">
        <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); try { localStorage.setItem("malair_docs_project", v); } catch {} }}>
          <SelectTrigger className="bg-[#0d1218] border-white/[0.06] text-white w-56" data-testid="doc-project-select">
            <FolderKanban className="w-4 h-4 mr-2 text-white/40" />
            <SelectValue placeholder="Välj projekt" />
          </SelectTrigger>
          <SelectContent className="bg-[#0d1218] border-white/[0.06]">
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-white">
                {p.name} {p.work_number && <span className="text-white/40 ml-1">({p.work_number})</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Beskrivning (valfritt)"
          className="bg-[#0d1218] border-white/[0.06] text-white flex-1"
        />

        <label className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors text-sm font-medium",
          uploading ? "bg-[#333] text-white/40" : "bg-[#6dbf4a] hover:bg-[#5da83d] text-white"
        )}>
          <Upload className="w-4 h-4" />
          {uploading ? "Laddar..." : "Ladda upp"}
          <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.zip" className="hidden"
            onChange={handleUpload} disabled={uploading || !selectedProject} data-testid="file-input" />
        </label>
      </div>

      {/* Tabs */}
      {selectedProject && (
        <div className="flex items-center gap-1 bg-[#0a0e14] p-1 rounded-xl border border-white/[0.06] w-fit">
          <button
            onClick={() => setActiveTab("images")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
              activeTab === "images"
                ? "bg-[#6dbf4a]/15 text-[#6dbf4a] shadow-sm"
                : "text-white/40 hover:text-white/60"
            )}
            data-testid="tab-images"
          >
            <Camera className="w-4 h-4" />
            Bilder
            {images.length > 0 && (
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full font-mono",
                activeTab === "images" ? "bg-[#6dbf4a]/20 text-[#6dbf4a]" : "bg-white/10 text-white/40"
              )}>{images.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("files")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
              activeTab === "files"
                ? "bg-[#6dbf4a]/15 text-[#6dbf4a] shadow-sm"
                : "text-white/40 hover:text-white/60"
            )}
            data-testid="tab-files"
          >
            <FolderOpen className="w-4 h-4" />
            Filer
            {files.length > 0 && (
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full font-mono",
                activeTab === "files" ? "bg-[#6dbf4a]/20 text-[#6dbf4a]" : "bg-white/10 text-white/40"
              )}>{files.length}</span>
            )}
          </button>
        </div>
      )}

      {/* Project info */}
      {currentProject && (
        <div className="flex items-center gap-3 text-sm text-white/40">
          <span className="text-white font-medium">{currentProject.name}</span>
          {currentProject.work_number && (
            <span className="text-[#6dbf4a] font-mono bg-[#6dbf4a]/10 px-1.5 py-0.5 rounded text-xs">{currentProject.work_number}</span>
          )}
          <span>{documents.length} {documents.length === 1 ? "fil" : "filer"} totalt</span>
        </div>
      )}

      {/* === BILDER TAB === */}
      {activeTab === "images" && (
        <>
          {/* Tag search for images */}
          {images.length > 0 && (
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                value={imageSearch}
                onChange={(e) => setImageSearch(e.target.value)}
                placeholder="Sök på taggar..."
                className="bg-[#0d1218] border-white/[0.06] text-white pl-9 text-sm"
                data-testid="image-search"
              />
            </div>
          )}

          {(() => {
            const filtered = imageSearch.trim()
              ? images.filter(d => d.ai_tags?.some(t => t.toLowerCase().includes(imageSearch.toLowerCase())))
              : images;

            if (filtered.length > 0) {
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {filtered.map(doc => {
                    const token = localStorage.getItem("token");
                    const thumbUrl = `${documentsApi.download(doc.id)}?token=${token}`;
                    return (
                      <div key={doc.id} className="group relative bg-[#0d1218] rounded-xl overflow-hidden border border-white/[0.06] hover:border-[#6dbf4a]/50 transition-all cursor-pointer"
                        onClick={() => handlePreview(doc)} data-testid={`doc-${doc.id}`}>
                        <div className="aspect-square bg-[#080c12] flex items-center justify-center overflow-hidden">
                          <img
                            src={thumbUrl}
                            alt={doc.filename}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentNode.innerHTML = '<div class="flex items-center justify-center w-full h-full"><svg class="w-10 h-10 text-white/25" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>';
                            }}
                          />
                        </div>
                        <div className="p-2.5">
                          <p className="text-xs text-white truncate font-medium">{doc.filename}</p>
                          {doc.description && <p className="text-[11px] text-white/40 truncate">{doc.description}</p>}
                          {doc.ai_tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {doc.ai_tags.slice(0, 3).map((tag, ti) => (
                                <span key={ti} className="text-[10px] px-1.5 py-0.5 rounded bg-[#6dbf4a]/10 text-[#6dbf4a]/70">{tag}</span>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-[10px] text-white/25">{formatFileSize(doc.file_size)}</span>
                            <span className="text-[10px] text-white/25">{doc.uploaded_by_name}</span>
                          </div>
                        </div>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button className="p-2 bg-white/20 rounded-xl hover:bg-white/30"><Eye className="w-5 h-5 text-white" /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleDownload(doc); }} className="p-2 bg-white/20 rounded-xl hover:bg-white/30"><Download className="w-5 h-5 text-white" /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }} className="p-2 bg-red-500/30 rounded-xl hover:bg-red-500/50"><Trash2 className="w-5 h-5 text-white" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            if (imageSearch.trim() && images.length > 0) {
              return (
                <div className="text-center py-12">
                  <Search className="w-8 h-8 mx-auto mb-3 text-gray-700" />
                  <p className="text-white/40 text-sm">Inga bilder matchar "{imageSearch}"</p>
                </div>
              );
            }

            return (
              <div className="text-center py-16">
                <Camera className="w-10 h-10 mx-auto mb-3 text-gray-700" />
                <p className="text-white/40 text-sm">Inga bilder ännu</p>
                <p className="text-white/25 text-xs mt-1">Ladda upp foton eller ritningar</p>
              </div>
            );
          })()}
        </>
      )}

      {/* === FILER TAB === */}
      {activeTab === "files" && (
        <>
          {files.length > 0 ? (
            <div className="space-y-1">
              {files.map(doc => {
                const IconComp = FILE_ICONS[getFileType(doc.filename)] || FILE_ICONS.default;
                return (
                  <div key={doc.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#0d1218] hover:bg-[#111820] transition-colors group cursor-pointer"
                    onClick={() => handlePreview(doc)}
                    data-testid={`doc-${doc.id}`}>
                    <div className="w-9 h-9 rounded-xl bg-[#6dbf4a]/10 flex items-center justify-center flex-shrink-0">
                      <IconComp className="w-5 h-5 text-[#6dbf4a]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate font-medium">{doc.filename}</p>
                      <div className="flex items-center gap-2 text-[11px] text-white/40">
                        {doc.description && <span>{doc.description} ·</span>}
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>· {doc.uploaded_by_name}</span>
                        <span>· {format(new Date(doc.uploaded_at), "d MMM", { locale: sv })}</span>
                      </div>
                      {doc.ai_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {doc.ai_tags.map((tag, ti) => (
                            <span key={ti} className="text-[10px] px-1.5 py-0.5 rounded bg-[#6dbf4a]/10 text-[#6dbf4a]/70">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-white"
                        onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}><Download className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-red-400"
                        onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-10 h-10 mx-auto mb-3 text-gray-700" />
              <p className="text-white/40 text-sm">Inga filer ännu</p>
              <p className="text-white/25 text-xs mt-1">Ladda upp PDF:er, CAD-filer eller andra dokument</p>
            </div>
          )}
        </>
      )}

      {/* Document Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => { setPreviewUrl(null); setPreviewError(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-[#080c12] border-white/[0.06] p-0 gap-0 overflow-hidden [&>button]:hidden">
          {/* Custom close header - always visible */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <span className="text-white text-sm font-medium truncate flex-1 mr-4">{previewName}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="ghost" size="icon"
                className="h-9 w-9 text-white/40 hover:text-white hover:bg-white/10"
                onClick={() => { if (previewUrl) { const a = document.createElement("a"); a.href = previewUrl; a.download = previewName; a.click(); } }}>
                <Download className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon"
                className="h-9 w-9 text-white/60 hover:text-white hover:bg-white/10"
                onClick={() => { setPreviewUrl(null); setPreviewError(false); }}
                data-testid="close-preview-btn">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          {previewUrl && (
            <div className="flex flex-col items-center overflow-auto" style={{ maxHeight: 'calc(90vh - 60px)' }}>
              {previewError ? (
                <div className="flex flex-col items-center gap-4 py-12">
                  <FileText className="w-16 h-16 text-red-400/30" />
                  <p className="text-red-400 text-sm font-medium">Filen kunde inte laddas</p>
                  <p className="text-white/30 text-xs text-center max-w-sm">
                    Filen kan ha forsvunnit vid en deploy. Ladda upp den igen.
                  </p>
                </div>
              ) : getFileType(previewName) === "image" ? (
                <img src={previewUrl} alt={previewName} className="max-w-full object-contain" style={{ maxHeight: 'calc(90vh - 60px)' }}
                  onError={() => setPreviewError(true)} />
              ) : previewName.toLowerCase().endsWith('.pdf') ? (
                <iframe src={previewUrl} title={previewName} className="w-full" style={{ height: 'calc(90vh - 60px)' }} />
              ) : (
                <div className="flex flex-col items-center gap-4 py-12">
                  <FileText className="w-16 h-16 text-white/20" />
                  <p className="text-white/40 text-sm">{previewName}</p>
                  <Button onClick={() => { const a = document.createElement("a"); a.href = previewUrl; a.download = previewName; a.click(); }}
                    className="bg-[#6dbf4a] hover:bg-[#5da83d] text-white gap-2">
                    <Download className="w-4 h-4" /> Ladda ner
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
